
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { User } from "@supabase/supabase-js";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export type ProfileFormValues = {
  first_name: string;
  last_name: string;
  email: string;
  companyName?: string;
  phoneNumber?: string;
};

export const useProfileForm = (user: User | null) => {
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Initialize the form with empty values
  const { register, handleSubmit, setValue, formState: { errors } } = useForm<ProfileFormValues>({
    defaultValues: {
      first_name: "",
      last_name: "",
      email: "",
      companyName: "",
      phoneNumber: ""
    }
  });

  // Update form values when user data is available
  useEffect(() => {
    if (!user) {
      setLoadError("User data not available");
      return;
    }
    
    try {
      setLoadError(null);
      
      console.log("Setting profile form values from user:", user);
      setValue("email", user.email || "");
      setValue("first_name", user.user_metadata?.first_name || "");
      setValue("last_name", user.user_metadata?.last_name || "");
      setValue("companyName", user.user_metadata?.companyName || "");
      setValue("phoneNumber", user.user_metadata?.phoneNumber || "");
    } catch (error) {
      console.error("Error setting profile form values:", error);
      setLoadError("Could not load profile information");
    }
  }, [user, setValue]);

  // Submit handler
  const onSubmit = async (data: ProfileFormValues) => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to update your profile",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    
    try {
      const { data: updateData, error } = await supabase.auth.updateUser({
        email: data.email,
        data: {
          first_name: data.first_name,
          last_name: data.last_name,
          companyName: data.companyName,
          phoneNumber: data.phoneNumber
        }
      });

      if (error) {
        throw error;
      }

      toast({
        title: "Profile Updated",
        description: "Your profile has been successfully updated."
      });
    } catch (error) {
      console.error("Error updating profile:", error);
      toast({
        title: "Update Failed",
        description: "There was an error updating your profile. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  return {
    register,
    handleSubmit,
    errors,
    onSubmit,
    isSaving,
    loadError
  };
};
